import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Container, Paper, Typography, TextField, Button, Stepper, Step, StepLabel, Divider, Alert, CircularProgress, IconButton, InputAdornment, Card, CardContent} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Visibility,
  VisibilityOff,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  Schedule as ScheduleIcon,
  MedicalServices as MedicalServicesIcon,
  Person as PersonIcon,
  VerifiedUser as VerifiedUserIcon,
  ArrowBack,
  Info,
  Search as SearchIcon,
  ContentCopy as CopyIcon} from '@mui/icons-material';
import { authService } from '@services/authService';
import { companiesDGService } from '@services/companiesDGService';
import { API_BASE_URL } from '@config/api';
import SimpleAddressForm from '@shared/SimpleAddressForm';
import CuiAutocomplete from '@shared/CuiAutocomplete';
import CompanyCompactCard from '@shared/CompanyCompactCard';
import ServicesField from '@shared/ServicesField';


// Functie pentru separarea strazii si numarului din adresa
function splitStreetAndNumber(input: string) {
  // exemple de formate posibile: "Str. Lalelelor 10", "Bulevardul Unirii nr. 12A", "Calea Victoriei, Nr. 5"
  if (!input) return { street: '', number: '' };
  const cleaned = input.replace(/\s+,/g, ',').trim();

  // cauta "nr", "nr.", sau numere la finalul strazii
  const nrRegex = /\b(?:nr\.?|numar(?:ul)?)\s*([0-9]+[A-Za-z\-\/]*)\b/i;
  const endNumberRegex = /(.+?)\s+([0-9]+[A-Za-z\-\/]*)$/;

  const nrMatch = cleaned.match(nrRegex);
  if (nrMatch) {
    const number = nrMatch[1].trim();
    const street = cleaned.replace(nrRegex, '').replace(/\s{2}/g, ' ').replace(/,\s*$/, '').trim();
    return { street, number };
  }

  const endMatch = cleaned.match(endNumberRegex);
  if (endMatch) {
    return { street: endMatch[1].trim().replace(/,\s*$/, ''), number: endMatch[2].trim() };
  }

  // fallback: totul e strada, fara numar
  return { street: cleaned, number: '' };
}

const steps = ['Verificare CUI', 'Date financiare ANAF', 'Informatii de baza', 'Adresa si contact', 'Orar si servicii', 'Cont administrator'];

// Helper functions
const formatRegCom = (regCom: string): string => {
  if (!regCom) return '—';
  return regCom.replace(/(\d{2})(\d{2})(\d{4})/, '$1/$2/$3');
};

const formatDate = (dateStr: string): string => {
  if (!dateStr) return '—';
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('ro-RO');
  } catch {
    return dateStr;
  }
};

const maskIBAN = (iban: string): string => {
  if (!iban) return '—';
  if (iban.length < 8) return iban;
  return iban.substring(0, 4) + '****' + iban.substring(iban.length - 4);
};

const getStareColor = (stare: string): string => {
  if (!stare) return 'grey.400';
  const lowerStare = stare.toLowerCase();
  if (lowerStare.includes('activ') || lowerStare.includes('inregistrat')) return 'success.main';
  if (lowerStare.includes('radiat') || lowerStare.includes('anulat')) return 'error.main';
  if (lowerStare.includes('suspendat') || lowerStare.includes('inactiv')) return 'warning.main';
  return 'info.main';
};

const RegisterClinic: React.FC = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [cuiVerified, setCuiVerified] = useState(false);
  const [cuiVerifying, setCuiVerifying] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [anafData, setAnafData] = useState<any>(null);

  // Handler functions
  const handleRefreshANAF = async () => {
    if (!formData.cui) return;
    try {
      setCuiVerifying(true);
      const response = await companiesDGService.getCompanyDG(formData.cui);
      if (response.success) {
        setAnafData(response.data);
      }
    } catch (error) {
      console.error('Eroare la reimprospatarea datelor ANAF:', error);
    } finally {
      setCuiVerifying(false);
    }
  };

  const handleCopyField = (fieldName: string, value: string) => {
    navigator.clipboard.writeText(value).then(() => {
      // Toast notification could be added here
    }).catch(() => {
      // Eroare la copierea in clipboard
    });
  };

  const _handleROEFacturaChange = (checked: boolean) => {
    // Handle RO e-Factura checkbox change
  };

  const [formData, setFormData] = useState({
    // CUI si nume clinica
    cui: '',
    clinicName: '',
    description: '',

    // Adresa sediului social (din ONRC)
    headquartersAddress: {
      street: '',
      streetNumber: '',
      city: '',
      county: '',
      postalCode: '',
      country: 'Romania',
      sector: ''
    },

    // Adresa punctului de lucru (poate fi diferita de sediul social)
    workAddress: {
      street: '',
      streetNumber: '',
      city: '',
      county: '',
      postalCode: '',
      country: 'Romania',
      sector: ''
    },
    
    // Contact
    phone: '',
    email: '',
    website: '',

    // Orar ?i servicii
    schedule: {
      monday: { start: '09:00', end: '18:00', closed: false },
      tuesday: { start: '09:00', end: '18:00', closed: false },
      wednesday: { start: '09:00', end: '18:00', closed: false },
      thursday: { start: '09:00', end: '18:00', closed: false },
      friday: { start: '09:00', end: '18:00', closed: false },
      saturday: { start: '09:00', end: '14:00', closed: false },
      sunday: { start: '09:00', end: '14:00', closed: false }
    },
    services: [] as string[],

    // Cont administrator
    adminFirstName: '',
    adminLastName: '',
    adminEmail: '',
    adminPhone: '',
    password: '',
    confirmPassword: '',
    
    // Flag pentru tracking
    fromInfoCui: false
  });

  // Handler pentru selectarea unei companii din ONRC/ANAF
  const handleONRCCompanySelect = async (company: any) => {

    // Gestionare pentru date din ANAF (structura noua cu date generale)
    if (company.CUI && company.denumireCompanie) {
      // Salveaza datele ANAF pentru afisare in pagina financiara
      setAnafData(company);
      
      // Date din ANAF DG - foloseste proprietatile noi
      const formatted = company.adresaDomiciliuFiscalText || '';
      const sediuSocial = company.sediuSocial || {};
      const country = 'Romania';

      // Foloseste datele structurate din sediuSocial
      
      let street = sediuSocial.strada || '';
      let number = sediuSocial.numar || '';
      let city = sediuSocial.localitate || '';
      let county = sediuSocial.judet || '';
      let postalCode = sediuSocial.codPostal || company.codPostal || '';
      
      
      // Fallback: daca campurile structurate sunt goale SAU contin valori gresite, parseaza din adresa completa
      const hasInvalidValues = (city && (city.includes('Bl.') || city.includes('Sc.'))) || 
                              (county && (county.includes('Bl.') || county.includes('Sc.')));
      
      if ((!street && !city && !county && formatted) || hasInvalidValues) {
        
        // Parseaza adresa text: "MUNICIPIUL BUCUREŞTI, SECTOR 6, STR MUNIŢIEI, NR.5, CAMERA 1, ET.3, AP.9"
        const parts = formatted.split(',').map((p: string) => p.trim());
        
        // Gaseste Bucuresti
        const bucurestPart = parts.find((p: string) => /MUNICIPIUL\s*BUCURESTI|BUCURESTI/i.test(p));
        if (bucurestPart) {
          city = 'Bucuresti';
          county = 'Bucuresti';
        }
        
        // Gaseste sectorul pentru Bucuresti
        const sectorPart = parts.find((p: string) => /SECTOR\s*\d+/i.test(p));
        if (sectorPart && city === 'Bucuresti') {
          const sectorMatch = sectorPart.match(/SECTOR\s*(\d+)/i);
          if (sectorMatch) {
            county = `Sector ${sectorMatch[1]}`;
          }
        }
        
        // Gaseste strada
        const streetPart = parts.find((p: string) => /\bSTR\.?\s+/i.test(p));
        if (streetPart) {
          const streetMatch = streetPart.match(/STR\.?\s+([^,]+)/i);
          if (streetMatch) {
            street = streetMatch[1].trim();
          }
        }
        
        // Gaseste numarul
        const numberPart = parts.find((p: string) => /\bNR\.?\s*\d+/i.test(p));
        if (numberPart) {
          const numberMatch = numberPart.match(/NR\.?\s*(\d+)/i);
          if (numberMatch) {
            number = numberMatch[1];
          }
        }
      }
      

      // Extrage sectorul pentru Bucuresti si curata orasul
      let sector = '';
      let cleanCity = city;
      
      if (city.toLowerCase().includes('bucuresti') || city.toLowerCase().includes('bucuresti') || city.toLowerCase().includes('bucureşti') || 
          county.toLowerCase().includes('sector')) {
        const sectorMatch = county.match(/sector\s*(\d+)/i) || city.match(/sector\s*(\d+)/i);
        if (sectorMatch) {
          sector = `Sector ${sectorMatch[1]}`;
          // Curata orasul de sector - pastreaza doar "Bucuresti"
          cleanCity = city.replace(/sector\s*\d+\s*/i, '').replace(/mun\.?\s*/i, '').trim();
        }
      }

    setFormData(prev => ({
      ...prev,
        clinicName: company.denumireCompanie || prev.clinicName || '',
      headquartersAddress: {
          street: street,
          streetNumber: number,
          city: cleanCity,
          county: county,
          postalCode: postalCode,
          country: country,
          sector: sector
      },
      workAddress: prev.workAddress, // nu atinge aici
        phone: company.telefon || prev.phone || '',
        email: company.email || prev.email || '',
        website: company.website || prev.website || '',
        fromInfoCui: true
      }));

      return;
    }

    // Daca ajungem aici, inseamna ca datele nu sunt din ANAF DG
  };

  // Handler pentru autocompletarea adresei punctului de lucru cu adresa sediului social
  const handleCopyHeadquartersToWorkAddress = () => {
    setFormData(prev => ({
      ...prev,
      workAddress: {
        ...prev.headquartersAddress,
        // Asigura-te ca toate campurile sunt copiate, inclusiv codul postal
        postalCode: prev.headquartersAddress.postalCode,
        country: prev.headquartersAddress.country || 'Romania'
      }
    }));
    
  };

  // Handler pentru modificarea adresei punctului de lucru
  const handleWorkAddressChange = (address: any) => {
    setFormData(prev => ({
      ...prev,
      workAddress: address
    }));
  };

  // Handler pentru modificarea adresei sediului social
  const handleHeadquartersAddressChange = (address: any) => {
    setFormData(prev => ({
      ...prev,
      headquartersAddress: address
    }));
  };

  // Restul func?iilor raman la fel...
  const verifyCui = async () => {
    const cui = (formData.cui || '').trim().replace(/\D/g, ''); // doar cifre
    if (!cui) {
      setError('CUI-ul este obligatoriu');
      return;
    }

    setCuiVerifying(true);
    setError('');

    try {
      // 1. Verifica daca CUI-ul nu a fost gasit in baza de date locala
      try {
        const response = await authService.checkClinicCui(cui);
        
        // Daca avem eroare de validare sau server, afiseaza mesajul
        if (!response.success && response.error) {
          setError(response.error);
          return;
        }
        
        // Daca CUI-ul exista deja in platforma
        if (response.exists) {
          setError('❌ Acest CUI este deja inregistrat in sistem. Nu poti continua cu inregistrarea.');
          return; // Blocheaza inregistrarea
        }
        
        // CUI-ul a fost verificat cu succes (nu exista deja)
        setCuiVerified(true);
      } catch (dbError: any) {
        const errorCode = dbError?.response?.data?.code;
        if (errorCode === 'CUI_NOT_FOUND') {
          // CUI-ul nu exista in baza de date locala, continuam cu verificarea
          setCuiVerified(true);
        } else {
          // Alta eroare
          setError('Eroare la verificarea CUI-ului. incerca?i din nou.');
          return;
        }
      }

            // 2. Cauta datele la ANAF
            try {
              const anafData = await companiesDGService.getCompanyDG(cui);
              setAnafData(anafData.data);
              
            // Pre-completeaza formularul cu datele ANAF
            // Parseaza adresa manual daca sediuSocial nu este complet
            let street = anafData.data.sediuSocial?.strada || '';
            let streetNumber = anafData.data.sediuSocial?.numar || '';
            let city = anafData.data.sediuSocial?.localitate || '';
            let county = anafData.data.sediuSocial?.judet || '';
            let postalCode = anafData.data.sediuSocial?.codPostal || anafData.data.codPostal || '';
            
            
            // Daca adresa nu este completa, parseaza din adresaDomiciliuFiscalText
            if (!street || !city || city === 'Sector 6') {
              const addressText = anafData.data.adresaDomiciliuFiscalText || '';
              
              // Parseaza adresa: "MUNICIPIUL BUCUREŞTI, SECTOR 6, STR SERGENT CUTIERU ALEXANDRU, NR.25A, BL.1, SC.1, ET.8, AP.96"
              const parts = addressText.split(',').map(p => p.trim());
              
              // Detecteaza Bucuresti
              if (addressText.includes('BUCUREŞTI') || addressText.includes('BUCURESTI')) {
                city = 'Bucuresti';
                county = 'Bucuresti';
              }
              
              // Detecteaza strada - cauta partea cu STR
              const streetPart = parts.find(p => p.includes('STR') && !p.includes('NR.'));
              if (streetPart) {
                // Extrage numele strazii dupa STR. sau STR
                const streetMatch = streetPart.match(/STR\.?\s+(.+)/i);
                if (streetMatch) {
                  street = streetMatch[1].trim();
        } else {
                }
              }
              
              // Detecteaza numarul - cauta partea cu NR.
              const numberPart = parts.find(p => p.includes('NR.'));
              if (numberPart) {
                const numberMatch = numberPart.match(/NR\.?\s*([0-9A-Za-z]+)/i);
                if (numberMatch) {
                  streetNumber = numberMatch[1];
                }
              }
              
            }
            
            setFormData(prev => ({
              ...prev,
              clinicName: anafData.data.denumireCompanie || prev.clinicName || '',
              headquartersAddress: {
                street: street,
                streetNumber: streetNumber,
                city: city,
                county: county,
                postalCode: postalCode,
                country: anafData.data.sediuSocial?.tara || 'Romania',
                sector: ''
              },
              workAddress: prev.workAddress,
              phone: anafData.data.telefon || prev.phone || '',
              email: prev.email || '',
              website: prev.website || '',
              fromInfoCui: true
            }));
            } catch (anafError: any) {
              console.error('❌ Eroare la interogarea ANAF:', anafError);
              setError(`Nu am putut prelua datele din ANAF: ${anafError.message}`);
              return;
            }

      // 3. CUI-ul a fost verificat cu succes
      setError('');
      
      // Avansam automat la pasul urmator (pagina cu datele ANAF)
      setActiveStep(1);
    } catch (err: any) {
      console.error(' Eroare la verificarea CUI:', err);
      const errorMessage = err?.response?.data?.error || err.message;
      const errorCode = err?.response?.data?.code;
      
      if (errorCode === 'CUI_NOT_FOUND') {
        setError('CUI-ul nu a fost gasit in baza de date ONRC. Verifica?i numarul introdus.');
      } else {
        // Alta eroare
        setError('Eroare la verificarea CUI-ului. incerca?i din nou.');
      }
    } finally {
      setCuiVerifying(false);
    }
  };

  const validateStep = (step: number) => {
    switch (step) {
      case 0:
        return cuiVerified;
      case 1:
        return !!anafData; // Datele ANAF trebuie sa fie disponibile
      case 2:
        return !!(formData.clinicName && formData.clinicName.trim().length > 0);
      case 3:
        return !!(formData.workAddress.street && formData.workAddress.city && formData.workAddress.county);
      case 4:
        return true; // Orarul este op?ional
      case 5:
        const isValid = !!(
          formData.adminFirstName &&
          formData.adminFirstName.trim().length > 0 &&
          formData.adminLastName &&
          formData.adminLastName.trim().length > 0 &&
          formData.adminEmail &&
          formData.adminEmail.trim().length > 0 &&
          formData.password &&
          formData.password.trim().length > 0 &&
          formData.confirmPassword &&
          formData.confirmPassword.trim().length > 0 &&
          formData.password === formData.confirmPassword
        );
        
        return isValid;
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const handleSubmit = async () => {
    if (!validateStep(activeStep)) {
      setError('Toate campurile obligatorii trebuie completate.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const clinicData = {
        clinic: {
          name: formData.clinicName,
          cui: formData.cui,
          description: formData.description,
          address: formData.workAddress, // Folosim adresa punctului de lucru ca adresa principala
          phone: formData.phone,
          email: formData.email,
          website: formData.website,
          workingHours: formData.schedule,
          services: formData.services,
          fromInfoCui: formData.fromInfoCui // <--- nu uita flag-ul
        },
        admin: {
          firstName: formData.adminFirstName,
          lastName: formData.adminLastName,
          name: `${formData.adminFirstName} ${formData.adminLastName}`, // <--- compat
          email: formData.adminEmail,
          phone: formData.adminPhone,
          password: formData.password
        }
      };


      const response = await fetch(`${API_BASE_URL}/auth/register-clinic`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'},
        body: JSON.stringify(clinicData)});

      const result = await response.json();

      if (!response.ok) {
        setError(result.error || result.message || 'Eroare la inregistrare');
        return;
      }
      if (result.success) {
        setSuccess('Inregistrarea a fost trimisa cu succes! Vei fi redirectionat la login...');
        setTimeout(() => navigate('/login'), 2500);
      } else {
        setError(result.error || 'Eroare la inregistrare');
      }
    } catch (err: any) {
      console.error(' Eroare detaliata:', err);
      setError(err.response?.data?.error || 'Eroare la inregistrare');
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
              <BusinessIcon sx={{ mr: 1, verticalAlign: 'middle', color: 'primary.main' }} />
              Verificare CUI Clinica
            </Typography>

            <Alert severity="info" sx={{ mb: 2 }}>
              <Info sx={{ mr: 1 }} />
              Introduceti codul unic de identificare (numeric) pentru a completa automat datele din ONRC.
            </Alert>

            <Grid container spacing={3}>
              <Grid size={12}>
                <CuiAutocomplete
                  value={formData.cui}
                  onChange={(cui) => setFormData(prev => ({ ...prev, cui }))}
                  onCompanySelect={handleONRCCompanySelect}
                  label="CUI clinica"
                  placeholder="Ex: 12345678"
                  autoSearch={false}
                  required
                  disabled={cuiVerified}
                />
              </Grid>
            </Grid>

            {cuiVerified && (
              <Alert severity="success" sx={{ mt: 3, backgroundColor: 'success.light', border: '1px solid', borderColor: 'success.main' }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                   CUI verificat cu succes! Datele au fost pre-completate din ONRC.
                </Typography>
              </Alert>
            )}
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, color: 'primary.main' }}>
              📋 Verificare date ANAF
            </Typography>
            
            {anafData ? (
              <CompanyCompactCard
                company={anafData}
                onCopy={handleCopyField}
              />
            ) : (
              <Alert severity="error">
                Nu s-au putut incarca datele ANAF. Te rugam sa revii la pasul anterior.
              </Alert>
            )}
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
              <BusinessIcon sx={{ mr: 1, verticalAlign: 'middle', color: 'primary.main' }} />
              Informatii de baza
            </Typography>

            <Grid container spacing={3}>
              <Grid size={12}>
                <TextField
                  fullWidth
                  label="Numele clinicii"
                  value={formData.clinicName}
                  onChange={(e) => setFormData(prev => ({ ...prev, clinicName: e.target.value }))}
                  required
                />
              </Grid>

              <Grid size={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Descriere clinica"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Descrie?i serviciile oferite de clinica..."
                />
              </Grid>

              <Grid size={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 2 }}>
                  <LocationIcon sx={{ mr: 1, verticalAlign: 'middle', color: 'primary.main' }} />
                  Adresa sediului social (din ONRC)
                </Typography>
                <SimpleAddressForm
                  value={formData.headquartersAddress}
                  onChange={handleHeadquartersAddressChange}
                  required
                />
              </Grid>
            </Grid>
          </Box>
        );

      case 3:
        return (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
              <LocationIcon sx={{ mr: 1, verticalAlign: 'middle', color: 'primary.main' }} />
              Adresa si contact
            </Typography>

            <Grid container spacing={3}>
              <Grid size={12}>
                <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                  Adresa punctului de lucru
                </Typography>

                <Box sx={{ mb: 2 }}>
                  <Button
                    variant="outlined"
                    startIcon={<CopyIcon />}
                    onClick={handleCopyHeadquartersToWorkAddress}
                    sx={{ mb: 2 }}
                  >
                    Copiaza adresa sediului social
                  </Button>
                </Box>

                <SimpleAddressForm
                  value={formData.workAddress}
                  onChange={handleWorkAddressChange}
                  required
                />
              </Grid>

              <Grid size={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                  Informatii de contact
                </Typography>
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Telefon"
                  value={formData.phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="Ex: 021 123 4567"
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="Ex: contact@clinica.ro"
                />
              </Grid>

              <Grid size={12}>
                <TextField
                  fullWidth
                  label="Website"
                  value={formData.website}
                  onChange={(e) => setFormData(prev => ({ ...prev, website: e.target.value }))}
                  placeholder="Ex: https://www.clinica.ro"
                />
              </Grid>
            </Grid>
          </Box>
        );

      case 4:
        return (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
              <ScheduleIcon sx={{ mr: 1, verticalAlign: 'middle', color: 'primary.main' }} />
              Orar si servicii
            </Typography>

            <Alert severity="info" sx={{ mb: 3 }}>
              <Info sx={{ mr: 1 }} />
              Orarul si serviciile sunt optionale si pot fi configurate ulterior.
            </Alert>

            <Grid container spacing={3}>
              <Grid size={12}>
                <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                  Orar de lucru
                </Typography>
                <Grid container spacing={2}>
                  {Object.entries(formData.schedule).map(([day, schedule]) => (
                    <Grid size={{ xs: 12, sm: 6, md: 4 }} key={day}>
                      <Card variant="outlined">
                        <CardContent sx={{ p: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ textTransform: 'capitalize' }}>
                              {day === 'monday' ? 'Luni' :
                               day === 'tuesday' ? 'Marti' :
                               day === 'wednesday' ? 'Miercuri' :
                               day === 'thursday' ? 'Joi' :
                               day === 'friday' ? 'Vineri' :
                               day === 'saturday' ? 'Sambata' : 'Duminica'}
                            </Typography>
                            <Button
                              size="small"
                              variant={schedule.closed ? "outlined" : "contained"}
                              color={schedule.closed ? "error" : "success"}
                              onClick={() => setFormData(prev => ({
                                ...prev,
                                schedule: {
                                  ...prev.schedule,
                                  [day]: { ...schedule, closed: !schedule.closed }
                                }
                              }))}
                            >
                              {schedule.closed ? 'inchis' : 'Deschis'}
                            </Button>
                          </Box>
                          <TextField
                            fullWidth
                            size="small"
                            type="time"
                            label="Ora de inceput"
                            value={schedule.start}
                            onChange={(e) => setFormData(prev => ({
                              ...prev,
                              schedule: {
                                ...prev.schedule,
                                [day]: { ...schedule, start: e.target.value }
                              }
                            }))}
                            disabled={schedule.closed}
                            InputLabelProps={{ shrink: true }}
                            sx={{ mb: 1 }}
                          />
                          <TextField
                            fullWidth
                            size="small"
                            type="time"
                            label="Ora de sfarsit"
                            value={schedule.end}
                            onChange={(e) => setFormData(prev => ({
                              ...prev,
                              schedule: {
                                ...prev.schedule,
                                [day]: { ...schedule, end: e.target.value }
                              }
                            }))}
                            disabled={schedule.closed}
                            InputLabelProps={{ shrink: true }}
                            sx={{ mb: 1 }}
                          />
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
              
              <Grid size={12}>
                <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                  Servicii oferite
                </Typography>
                <ServicesField
                  value={formData.services}
                  onChange={(services) => {
                    setFormData(prev => ({ ...prev, services }));
                  }}
                  fetchSuggestions={async (q) => {
                    try {
                      const res = await fetch(`/api/v1/services?q=${encodeURIComponent(q)}`);
                      const json = await res.json();
                      return json.options ?? [];
                    } catch (error) {
                      console.error('Eroare la incarcarea sugestiilor de servicii:', error);
                      return [];
                    }
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        );

      case 5:
        return (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
              <PersonIcon sx={{ mr: 1, verticalAlign: 'middle', color: 'primary.main' }} />
              Cont administrator
            </Typography>

            <Alert severity="info" sx={{ mb: 3 }}>
              <Info sx={{ mr: 1 }} />
              Creati contul de administrator pentru clinica. Acest cont va avea acces complet la toate functionalitatile.
            </Alert>

            <Grid container spacing={3}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Prenume"
                  value={formData.adminFirstName}
                  onChange={(e) => setFormData(prev => ({ ...prev, adminFirstName: e.target.value }))}
                  required
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Nume"
                  value={formData.adminLastName}
                  onChange={(e) => setFormData(prev => ({ ...prev, adminLastName: e.target.value }))}
                  required
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={formData.adminEmail}
                  onChange={(e) => setFormData(prev => ({ ...prev, adminEmail: e.target.value }))}
                  required
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Telefon"
                  value={formData.adminPhone}
                  onChange={(e) => setFormData(prev => ({ ...prev, adminPhone: e.target.value }))}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Parola"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                  required
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )}}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Confirma parola"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                  required
                  error={formData.password !== formData.confirmPassword && formData.confirmPassword !== ''}
                  helperText={formData.password !== formData.confirmPassword && formData.confirmPassword !== '' ? 'Parolele nu se potrivesc' : ''}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          edge="end"
                        >
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )}}
                />
              </Grid>
            </Grid>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom align="center" sx={{ fontWeight: 600, color: 'primary.main' }}>
            <MedicalServicesIcon sx={{ mr: 2, verticalAlign: 'middle', fontSize: '2rem' }} />
            inregistrare Clinica Veterinara
          </Typography>
          <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 3 }}>
            Completeaza formularul pentru a inregistra clinica ta veterinara in sistem
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        <Stepper
          activeStep={activeStep}
          alternativeLabel
          sx={{
            mb: 4,
            '& .MuiStepLabel-root': {
              '& .MuiStepLabel-label': {
                fontSize: '0.875rem',
                fontWeight: 500
              }
            },
            '& .MuiStep-root': {
              '& .MuiStepIcon-root': {
                fontSize: '24px',
                '&.Mui-active': {
                  color: 'primary.main'
                },
                '&.Mui-completed': {
                  color: 'success.main'
                }
              }
            }
          }}
        >
          {steps.map((label, index) => (
            <Step key={label}>
              <StepLabel
                StepIconComponent={({ active, completed }) => {
                  if (completed) return <VerifiedUserIcon sx={{ color: 'success.main' }} />;
                  if (active) return <BusinessIcon sx={{ color: 'primary.main' }} />;
                  return <BusinessIcon sx={{ color: 'grey.400' }} />;
                }}
              >
                {label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>

        <Card sx={{ mb: 2, borderRadius: 2, boxShadow: 2 }}>
          <CardContent sx={{ p: 3 }}>
            {renderStepContent(activeStep)}
          </CardContent>
        </Card>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          <Button
            onClick={handleBack}
            disabled={activeStep === 0}
            startIcon={<ArrowBack />}
            sx={{ minWidth: 120 }}
          >
            inapoi
          </Button>

          <Box sx={{ display: 'flex', gap: 2 }}>
            {activeStep === 0 && (
              <Button
                variant="contained"
                onClick={verifyCui}
                disabled={cuiVerifying || !formData.cui.trim()}
                startIcon={cuiVerifying ? <CircularProgress size={20} /> : <SearchIcon />}
                sx={{ minWidth: 140 }}
              >
                {cuiVerifying ? 'Verificare...' : 'Verifica CUI'}
              </Button>
            )}

            {activeStep === steps.length - 1 ? (
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={loading || !validateStep(activeStep)}
                startIcon={loading ? <CircularProgress size={20} /> : <VerifiedUserIcon />}
                sx={{ minWidth: 140 }}
              >
                {loading ? 'Se trimite...' : 'Finalizeaza'}
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={!validateStep(activeStep)}
                sx={{ minWidth: 120 }}
              >
                Urmatorul
              </Button>
            )}
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default RegisterClinic;











